import i18n from "@/libs/i18n";
import { ref } from "@vue/composition-api";
import axios from "@/libs/axios";

export default function useFacebook(channelData) {
    let FB;

    const pageNumber = ref(0);
    const defaultPage = ref({
        text: i18n.t("settings.channels.facebook.disconnected"),
        value: "disconnected",
        auth: "",
    });
    const selectedPage = ref({});
    const pages = ref([defaultPage.value]);
    const account = ref({
        picture: {
            data: {},
        },
    });
    const connected = ref(false);
    const disconnecting = ref(false);
    const originalPages = ref([]);
    const changingPage = ref(false);
    const facebookAppId = process.env.VUE_APP_FACEBOOK_APP_ID;

    const setupFacebook = (authResponse) => {
        return new Promise((resolve, reject) => {
            if (authResponse) {
                // To get a useful Facebook page token we need to get a long lived token
                axios
                    .post(`channels/${channelData.id}/connect`, {
                        userAccessToken: authResponse.accessToken,
                    })
                    .then((longLivedToken) => {
                        // Get the Facebook pages
                        FB.api("/me/accounts", { access_token: longLivedToken.data.access_token }, (response) => {
                            if (response.error) {
                                reject(response.error);
                            }

                            originalPages.value = response
                                ? response.data.map((page) => ({
                                      value: page.id,
                                      auth: page.access_token,
                                      text: page.name || i18n.t("settings.channels.facebook.no-access"),
                                      disabled: !page.name,
                                  }))
                                : [];

                            pages.value = [defaultPage.value, ...originalPages.value];
                            resolve(originalPages.value);
                        });
                    });

                // Get some basic facebook data
                FB.api(`/me?fields=name,id,picture.height(100).width(100)`, (response) => {
                    connected.value = true;

                    account.value = response;
                });
            } else {
                FB.logout();
                reject();
            }
        });
    };

    const checkFacebookToken = (tokenCheck) => {
        // First check if token is valid
        if (tokenCheck.error) {
            return false;
        }

        // Second check if token still has data access
        if (Date.now() / 1000 > tokenCheck.data_access_expires_at) {
            return false;
        }

        // Than check if token still has required permissions
        const requiredPermissions = ["pages_show_list", "pages_messaging", "pages_manage_metadata"];
        return requiredPermissions.every((permission) => tokenCheck.scopes.includes(permission));
    };

    const sdkInit = (FBObject) => {
        FB = FBObject.FB;

        // Do not continue if user is not yet logged in
        if (!channelData.auth || channelData.auth === "disconnected") {
            return;
        }

        // Check if token is still valid, if not force user to log in again
        axios
            .post(`channels/${channelData.id}/authenticate`, {
                facebookToken: channelData.auth,
            })
            .then((tokenCheck) => {
                if (!checkFacebookToken(tokenCheck.data.data)) {
                    FB.login(
                        async (response) => {
                            // Setup facebookPages like expected
                            await setupFacebook(response.authResponse);

                            // Update channel auth behind the scenes to save the new valid token
                            axios.patch(`channels/${channelData.id}`, {
                                auth: channelData.auth,
                                settings: [],
                            });
                        },
                        {
                            scope: "public_profile,email,pages_show_list,pages_manage_metadata,pages_messaging,pages_read_engagement",
                        }
                    );
                }
            });
    };

    const facebookLogin = (response) => {
        let authResponse = FB.getAuthResponse();

        // This is a fallback for non SSL sites like localhost
        if (!authResponse && response) {
            authResponse = response.authResponse;
        }

        return setupFacebook(authResponse);
    };

    const facebookLogout = () => {
        connected.value = false;
    };

    const updateConnection = (newIdentity) => {
        return new Promise(async (resolve, reject) => {
            changingPage.value = true;
            const newPage = pages.value.find((page) => page.value === newIdentity);
            disconnecting.value = newIdentity === "disconnected";

            try {
                // We need the old auth for disconnecting so this needs to be before we patch the channel
                if (disconnecting.value) {
                    await axios.post(`channels/${channelData.id}/disconnect`);
                }

                await axios.patch(`channels/${channelData.id}`, {
                    identity: newIdentity,
                    auth: newPage.auth,
                    settings: [],
                });

                // This is not how this should be done. Ideally you would use an emit, but that would require a refactor of the channel settings
                channelData.identity = newPage.value;
                channelData.auth = newPage.auth;

                // For connecting we need the new auth so we need to do this after the channel patch
                if (!disconnecting.value) {
                    await axios.post(`channels/${channelData.id}/connect`);
                }
            } catch {
                changingPage.value = false;
                return reject();
            }

            changingPage.value = false;

            resolve();
        });
    };

    return {
        FB,
        sdkInit,
        setupFacebook,
        checkFacebookToken,
        facebookLogin,
        facebookLogout,
        updateConnection,
        pageNumber,
        defaultPage,
        selectedPage,
        pages,
        originalPages,
        account,
        connected,
        facebookAppId,
        changingPage,
        disconnecting,
    };
}
